import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import UseGlobalConfirm from "Hooks/UseGlobalConfirm";
import UseNotification from "Hooks/UseNotification";
import { useFetch } from "Hooks/useFetch";

import Button from "Components/Admin/Buttons";
import ContainerWithTitle from "Components/Admin/ContainerWithTitle";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Loading from "Components/Admin/Loading";
import Preview from "Components/Admin/Modals/Preview";
import PageWrapper from "Components/Admin/PageWrapper";
import Section from "Components/Admin/Section";
import Table from "Components/Admin/Table";

import { buildColumns } from "./columns";
import * as S from "./styles";

const testEmailSchema = z.object({
  email: z
    .string()
    .min(1, "O e-mail é obrigatório")
    .email("Formato de e-mail inválido"),
});

const Configurations = () => {
  const { fetchData, loading: loadingSend } = useFetch();
  const { fetchData: fetchLogs, loading: loadingLogs } = useFetch();
  const { fetchData: fetchBody, loading: loadingBody } = useFetch();
  const { fetchData: fetchPurge, loading: loadingPurge } = useFetch();
  const { fetchData: fetchRetry, loading: loadingRetry } = useFetch();
  const { addGlobalConfirm } = UseGlobalConfirm();
  const { showNotification } = UseNotification();

  const [logs, setLogs] = useState(null);
  // Preview do corpo: { log } do registro aberto (null = modal fechado).
  const [bodyPreview, setBodyPreview] = useState(null);

  const methods = useForm({
    resolver: zodResolver(testEmailSchema),
    defaultValues: { email: "" },
  });

  const { register, reset } = methods;

  const submit = async (data) => {
    await fetchData({
      method: "post",
      path: "/configurations/send-test-email",
      body: { email: data.email },
      successMessage: "E-mail de teste enviado com sucesso!",
      onSuccessCallback: () => reset(),
      config: { show_success_message: true },
    });
  };

  const handleConsultLogs = async () => {
    await fetchLogs({
      method: "get",
      path: "/configurations/email-logs",
      queryParams: { page: 1, limit: 50 },
      onSuccessCallback: (data) => setLogs(data),
    });
  };

  // O corpo não vem na listagem: busca sob demanda pelo id da linha.
  const handleViewBody = async (row) => {
    setBodyPreview({ log: { ...row, body: null } });

    await fetchBody({
      method: "get",
      path: "/configurations/email-logs/:id/body",
      params: { id: row.id },
      onSuccessCallback: (data) => setBodyPreview({ log: data?.log ?? row }),
    });
  };

  const expiredBodies = logs?.expired_bodies_count ?? 0;
  // Envios que falharam e estão na fila. O job já reenvia sozinho com backoff;
  // este botão só antecipa, para quando o admin já sabe que a causa passou.
  const pendingEmails = logs?.pending_count ?? 0;

  const handleRetryPending = async () => {
    await fetchRetry({
      method: "post",
      path: "/configurations/retry-pending-emails",
      // A mensagem traz a contagem do que foi reprocessado — dinâmica, então
      // não cabe no `successMessage` (estático) do useFetch.
      onSuccessCallback: (data) => {
        showNotification(
          "success",
          "Reenvio de e-mails",
          data?.message ?? "Reenvio executado.",
        );
        handleConsultLogs();
      },
    });
  };

  const handlePurgeBodies = () => {
    addGlobalConfirm({
      type: "alert",
      title: "Remover corpos antigos",
      message: `Isso apaga o corpo de ${expiredBodies} e-mail(s) com mais de 90 dias. O restante do histórico (destinatário, assunto, template, status e data) é mantido.`,
      confirmText: "Remover",
      denyText: "Cancelar",
      closeOnClickBackground: true,
      onClickConfirm: async () => {
        await fetchPurge({
          method: "post",
          path: "/configurations/purge-email-log-bodies",
          // A mensagem vem do backend com a QUANTIDADE removida, então não dá
          // para usar o `successMessage` (estático) do useFetch.
          onSuccessCallback: (data) => {
            showNotification(
              "success",
              "Sucesso!",
              data?.message ?? "Corpos antigos removidos.",
            );
            handleConsultLogs();
          },
        });
      },
    });
  };

  return (
    <PageWrapper>
      <Section>
        <ContainerWithTitle icon="Settings" title="E-mail">
          <S.Wrapper>
            <ContainerWithTitle title="Teste de envio de e-mail">
              <FormWrapper {...methods} submit={submit}>
                <S.FormRow>
                  <Input
                    {...register("email")}
                    name="email"
                    label="E-mail"
                    placeholder="Digite o e-mail para teste"
                    type="email"
                  />
                  <Button
                    text="Enviar teste de e-mail"
                    type="submit"
                    loading={loadingSend}
                  />
                </S.FormRow>
              </FormWrapper>
            </ContainerWithTitle>

            <ContainerWithTitle title="Logs de e-mail">
              <S.LogsHeader>
                <span />
                <S.LogsActions>
                  {logs !== null && pendingEmails > 0 && (
                    <S.PendingInfo>
                      {pendingEmails} e-mail(s) aguardando reenvio
                    </S.PendingInfo>
                  )}

                  {logs !== null && pendingEmails > 0 && (
                    <Button
                      text="Reenviar agora"
                      type="button"
                      variant="light"
                      loading={loadingRetry}
                      onClick={handleRetryPending}
                    />
                  )}

                  {logs !== null && (
                    <S.ExpiredInfo>
                      {expiredBodies} corpo(s) com mais de 90 dias
                    </S.ExpiredInfo>
                  )}

                  {logs !== null && (
                    <Button
                      text="Remover corpos antigos"
                      type="button"
                      variant="light"
                      loading={loadingPurge}
                      disabled={expiredBodies === 0}
                      onClick={handlePurgeBodies}
                    />
                  )}

                  <Button
                    text="Abrir logs de e-mail"
                    type="button"
                    variant="light"
                    loading={loadingLogs}
                    onClick={handleConsultLogs}
                  />
                </S.LogsActions>
              </S.LogsHeader>

              {loadingLogs && <Loading />}

              {!loadingLogs && logs !== null && (
                <Table
                  data={logs.data ?? []}
                  columns={buildColumns({
                    onViewBody: handleViewBody,
                    maxAttempts: logs.max_attempts,
                  })}
                />
              )}
            </ContainerWithTitle>
          </S.Wrapper>
        </ContainerWithTitle>
      </Section>

      <Preview
        open={!!bodyPreview}
        title={`Corpo do e-mail — ${bodyPreview?.log?.template ?? ""}`}
        onClose={() => setBodyPreview(null)}
      >
        {loadingBody && <Loading />}

        {!loadingBody && bodyPreview?.log?.body ? (
          // HTML de e-mail renderizado dentro do painel: iframe com sandbox
          // vazio bloqueia script/form/navegação e isola o CSS da mensagem.
          <S.BodyFrame
            title="Corpo do e-mail"
            sandbox=""
            srcDoc={bodyPreview.log.body}
          />
        ) : null}

        {!loadingBody && !bodyPreview?.log?.body ? (
          <S.BodyEmpty>
            Corpo não disponível para este registro (log anterior à mudança ou
            já removido pelo expurgo).
          </S.BodyEmpty>
        ) : null}
      </Preview>
    </PageWrapper>
  );
};

export default Configurations;
